import { useState } from 'react';
import { Say } from '../widgets/Say';
import './pronounceEssay.css';
import { loadEnglishDictionaries } from '../../loader/loadEnglishDictionaries';

export function PronounceEssay() {
  const [init, setInit] = useState(false);
  if (!init) {
    setInit(true);
    loadEnglishDictionaries();
  }

  return (<div className='pronouncePage'>
    <h1>Pronouncing English is Hard</h1>

    <p>
      Let's say you wanted to explain to a non-English speaker how to pronounce any random English found in books or articles.
      How hard do you think that would be?
    </p>

    <p>
      Sure, it's well known that English spelling is horribly inconsistent,
      the result of centuries of changing pronunciations,
      using an alphabet that has fewer letters (26) than the language has sounds (roughly 40),
      and grabbing and modifying words from lots of different languages around the world
      while sometimes keeping the other language's spelling.
    </p>

    <p>
      But it turns out it's a lot harder than simply dealing with spelling.
      Below, you'll see some of the difficulties you'll encounter as you try to pronounce written English.
      To help give you useful context, you can click to hear text spoken when you
      see <Say>a triangle followed by underlined text</Say>, which will speak with a Western US accent.
    </p>

    <h2>Spelling</h2>

    <p>
      OK, let's first get spelling out of the way, since that's the most obvious problem.
      As some wag said, "English can be weird. <Say>It can be understood through tough thorough thought though.</Say>"
      Those are examples of spellings that are wildly inconsistent and seem to have no logic behind them whatsoever.
      So step 1 is to have a pronouncing dictionary that you can use to lookup a wide variety of words.
    </p>

    <p>
      But that doesn't completely solve the spelling problem because
      there are lots of personal and place names from around the world, lots of industry-specific jargon,
      and a nearly endless stream of new words being made up all the time.
      If a poet writes <Say>'Twas brillig, and the slithy toves did gyre and gimble in the wabe</Say>,
      an English speaker can guess how to pronounce all those words even though they aren't in the dictionary.
      So step 2 is that we need to have rules like "an <i>a</i> followed by a single consonant then an <i>e</i> turns into <Say say="/e̞i̯/">long a</Say>".
      Because spelling is so inconsistent, it may occasionally get it wrong, but at least this gets us close.
      And, as we'll see next, even figuring out which syllable to stress can be ambiguous.
    </p>

    <p>
      Fine, we have a dictionary and a set of rules to use for words not in the dictionary.
      Now that we have the easy part out of the way, let's get to some of the trickier issues.
    </p>

    <h2>Same spelling, different pronunciation</h2>

    <p>
      Consider the sentence <Say say="I live.V for live.ADJ concerts">I live for live concerts</Say>.
      Even though it uses the same spelling, <i>live</i> is pronounced two different ways in the same sentence.
      How do we know which pronunciation to use for each one?
      We say <Say say="live.V">live</Say> (with a so-called <Say say="[ɪ]">short i</Say>) when it's used as a verb
      and <Say say="live.ADJ">live</Say> (with a so-called <Say say="[aɪ̯]">long i</Say>) when it's used as used as an adjective or adverb.
      In this essay, we'll tack a part-of-speech abbreviation onto the word for clarity,
      for example <Say>live.V</Say> for the verb version, <Say>live.ADJ</Say> for the adjective, <Say>live.ADV</Say> for
      the adverb, and add .N if there's a noun version.
    </p>

    <p>
      English has lots of cases where the pronunciation changes by the part of speech, for example:
    </p>

    <ul>
      <li><Say>conduct.V</Say> (verb) versus <Say>conduct.N</Say> (noun)</li>
      <li><Say>contract.V.INTR</Say> (intransitive verb) versus <Say>contract</Say> (noun, transitive verb, adjective)</li>
      <li><Say>duplicate.V</Say> (verb) versus <Say>duplicate.N</Say> (noun)</li>
      <li><Say>number.N</Say> and <Say>number.V</Say> versus <Say>number.ADJ</Say> ("more numb", pronounced without the <i>b</i>)</li>
      <li><Say>produce.V</Say> (verb) versus <Say>produce.N</Say> (noun)</li>
      <li><Say>remake.V</Say> (verb) versus <Say>remake.N</Say> (noun)</li>
      <li>
        <Say>use.V</Say> (yooz - verb) versus <Say>use.N</Say> (yoos - noun),
        but also <Say say="it's /ˈjɯu̯zd/ for music">it's used for music</Say> (yoozd)
        versus <Say say="I /ˈjɯu̯st/ to play clarinet">I used to play clarinet</Say> (yoost).
      </li>
    </ul>

    <p>
      This means that you can't just blindly look up words in a dictionary or apply pronunciation rules.
      You have to understand how the word is being used in the sentence.
      In order to figure out how to say <i>contract</i> in&nbsp;
      <Say>the snake will contract.V.INTR before striking</Say> (intransitive verb)
      versus <Say>I'll contract.V.TR that work out</Say> (transitive verb),
      you need to understand enough English to see that <i>contract</i> in the first sentence is a verb with no object
      (is that <Say>object.V</Say> or <Say>object.N</Say>?).
    </p>

    <p>
      An even trickier set of letters is <i>lead</i>, where you need to know the <i>meaning</i> of the word in context as well as the part of speech.
      Consider <Say say="they took the leed">they took the lead.N</Say> versus <Say say="led is a soft metal">lead.N is a soft metal</Say>,
      both nouns but pronounced differently.
      Or <Say say="I'll leed the group">I'll lead.V the group</Say> versus <Say say="led the gasoline">lead.V the gasoline</Say> (add lead to the gasoline),
      both verbs but pronounced differently (fortunately the latter is rarely used).
      It can also be an adjective, as in <Say say="they're the leed manager on the project">they're the lead.ADJ manager on the project.N</Say>.
    </p>

    <p>
      Another type of quirk is that <i>have</i> can sometimes be pronounced with an <i>f</i> rather than a <i>v</i> when the word is stressed.
      Consider <Say say="%I /ˈhɛə̯f/ %too">I have to</Say> (with an f) versus <Say>I have two</Say> (with a v),
      which a dictionary might tell you sound the same.
    </p>

    <p>
      Suddenly, that simple mechanical lookup for pronunciation has turned into needing to understand parts of speech
      and sometimes even the meaning of the sentence itself.
    </p>

    <h2>Punctuation</h2>

    <p>
      Punctuation can offer additional hints for how to pronounce a sentence.
      Some symbols tell you to pause slightly, like a comma, colon, semi-colon, or various dashes,
      plus quotes, double-quotes, parentheses, square brackets, and curly brackets.
    </p>

    <p>
      And <Say>sentences often end with a period.</Say>&nbsp;
      You may not think about how your voice tends to drop at the end of the sentence, however.
      This isn't true of all languages, or even of all dialects of English,
      for example, <Say say="/wɛn ˈjɯu̯.zɪŋ ɐp˧tɑk˥/">when using uptalk</Say>.
      And <Say>how would you explain how your voice rises for a question?</Say>
    </p>

    <p>
      But the rules for when and how you pause aren't always obvious.
      Consider the following: <Say say="They are in the U.S. . Joe is in Canada.">They are in the U.S. Joe is in Canada.</Say>&nbsp;
      In this case, the periods in <i>U.S.</i> are there because it's an abbreviation for <i>United States</i>, not because it's the end of a sentence.
      However, <i>U.S.</i> actually <i>is</i> the end of this sentence, but how do we know?
      Sometimes you can figure this out when the following word is capitalized, since English capitalizes the first letter of a sentence.
      But English <i>also</i> capitalizes the first letter of a name, and <i>Joe</i> is a name.
      So...we're back to having to understand English thoroughly enough that we can infer that <i>Joe</i> starts a new sentence,
      because it doesn't make sense if we smash it all into a single sentence.
    </p>

    <p>
      Another example: <Say>I walked (I didn't run!) to the store</Say>.
      When you click on the sentence, the synthesizer spoke it as if <i>I didn't run!</i> was a complete sentence that needed to be followed by a full stop.
      But an English speaker would probably instead use a pause, closer to <Say>I walked, I didn't run, to the store</Say>.
    </p>

    <p>
      Or to riff on <i>us</i> some more, consider <Say say="That was *us in the picture">That was US in the picture</Say> versus <Say say="They are U.S. residents">They are US residents</Say>.
      In the first sentence, <i>us</i> is in all capitals because it's an alternate way to emphasize the word ("it was <i>us</i>, not you").
      In the second sentence, it's an alternate way of abbreviating <i>United States</i>.
      How can you tell the difference?
    </p>

    <h2>Numbers</h2>

    <p>
      In addition to letters and punctuation, you'll also encounter numeric digits.
      But that should be as easy as 1-2-3, right?
    </p>

    <p>
      First off, you obviously need to learn to pronounce numbers when the words aren't spelled out for you.
    </p>

    <ul>
      <li>Start with single digits, like <Say>1</Say> = one, <Say>2</Say> = two, <Say>3</Say> = three.</li>
      <li>Learn the irregular teens - <Say>11</Say> = eleven, <Say>12</Say> = twelve, <Say>13</Say> = thirteen,
        &nbsp;<Say>15</Say> = fifteen - and the regular teens -
        <Say>14</Say> = fourteen, <Say>16</Say> = sixteen.</li>
      <li>Learn the pattern for the rest of the numbers under 100 - <Say>21</Say> = twenty-one, <Say>32</Say> = thirty-two, <Say>43</Say> = fourty-three.</li>
      <li>Learn hundreds - <Say>123</Say> = one-hundred-and-twenty-three - and thousands - <Say>4567</Say> = four-thousand-five-hundred-and-sixty-seven</li>
      <li>Learn the pattern for the really large numbers - millions, billions, trillions, etc. - <Say>123,456,789</Say> = one-hundred-and-twenty-three-million,
        four-hundred-and-fifty-six-thousand, seven-hundred-and-eighty-nine.
        Oh, and notice the commas that occur every third digit.
        Alternately, some languages and regions choose to use periods instead, like 123.456.789.
      </li>
    </ul>

    <p>
      But even the basic numbers have some ambiguity.
      For example, how do you pronounce 1990?
      If you're counting, you would say one-thousand-nine-hundred-and-ninety (or perhaps nineteen-hundred-and-ninety):&nbsp;
      <Say say="There were one-thousand-nine-hundred-and-ninety people %there">There were 1990 people there</Say>.
      But if you're thinking about years, you would instead say nineteen-ninety: <Say>That show came out in 1990</Say>.
      Or - We saw <Say say="a hundred and one dalmations">101 (a hundred and one) Dalmations</Say> -
      versus  - My first college class was <Say say="psych 1 o 1">Psych 101 (one oh one)</Say>.
      These are other cases where you need to have a good understanding of the meaning of the sentence in order to know how to pronounce it.
    </p>

    <p>
      You'll see negative numbers, like -7, which you pronounce either as <Say>minus 7</Say> or <Say>negative 7</Say>.
      And decimals, where you say everything <i>before</i> the decimal point like a normal counting number,
      but you read off all the digits <i>after</i> the decimal point one at a time, so <Say>12.34</Say> = twelve-point-three-four.
      Or a negative decimal, like <Say>-45.678</Say>.
    </p>

    <p>
      Hmm, but recall that earlier we mentioned that some languages use a period for a separator in large numbers rather than a comma?
      Those languages also tend to use commas rather than a period for decimals,
      so then <Say say="-45678">-45.678</Say> = minus-forty-five-thousand-six-hundred-and-seventy-eight,
      while <Say say="2.3">2,3</Say> = two-point-three.
      You need to pay attention to the broader context of what you're reading to figure out if this might be the case.
    </p>

    <p>
      You'll also need to learn the <i>ordinals</i>, words like <i>first</i>, <i>second</i>, <i>third</i>, and <i>fourth</i>,
      which indicate rank or position,
      because you may see them written as <Say>1st</Say>, <Say>2nd</Say>, <Say>3rd</Say>, and <Say>4th</Say>.
    </p>

    <ul>
      <li>Most ordinals end with <i>th</i>, like <Say>6th</Say>, <Say>16th</Say>, and <Say>26th</Say>.</li>
      <li>But 1 usually ends with <i>st</i>, so you have <Say>1st</Say> and <Say>21st</Say>, but also <Say>11th</Say>.</li>
      <li>2 usually ends with <i>nd</i>, so you have <Say>2nd</Say> and <Say>22nd</Say>, but also <Say>12th</Say>.</li>
      <li>3 usually ends with <i>rd</i>, so you have <Say>3rd</Say> and <Say>23rd</Say>, but also <Say>13th</Say>.</li>
      <li>5 is <Say>fifth</Say> rather than <i>fiveth</i>, so you have <Say>5th</Say> and <Say>25th</Say>, but also <Say>15th</Say>.</li>
    </ul>

    <p>
      The ordinals are also used for fractions, such as <Say>1/5</Say> = one-fifth.
      And if you have more than one, the ordinal is plural: <Say>2/5</Say> = two-fifths.
      But <Say>1/2</Say> = one-half, not one-second, and <Say>3/2</Say> = three-halves, not three-halfs.
      And 1/4 could be <Say>1 4th</Say> or <Say>1 quarter</Say>.
    </p>

    <p>
      What do you say if you see something like 1/2.5?
      That's probably division, so you might say <Say>1 divided by 2.5</Say> or <Say>1 over 2.5</Say>.
      Hmm, so how did we know earlier that 1/2 should be read as a fraction and not division?
      Well, we didn't. We guessed, and we might have guessed wrong.
      You may be able to infer whether it's a fraction or division based on the surrounding text.
    </p>

    <h2>...and symbols</h2>

    <p>
      Numbers can occur with a wide variety of additional symbols, for example $ to indicate dollars.
      Though even a dollar sign comes with a few non-obvious wrinkles.
    </p>

    <ul>
      <li>The $ symbol comes first but is said last: <Say>$1</Say> = 1-dollar and <Say>$55</Say> = 55-dollars.</li>
      <li>
        But <Say say="1 million dollars">$1 million</Say> = 1-million-dollars, not 1-dollars-million.
        You need to move <i>dollars</i> after one or more other words that are part of the number.&nbsp;
        <Say say="1 hundred million dollars">$1 hundred million</Say> = 1-hundred-million-dollars.
      </li>
      <li>
        Make it plural when it's a noun - <Say>I have $90</Say> = I have 90 dollars
        - but not when it's an adjective - <Say say="A 90 dollar bottle of wine">A $90 bottle of wine</Say> = A 90 dollar bottle of wine.
      </li>
    </ul>

    <p>
      Examples of other symbols you might see with numbers:
    </p>

    <ul>
      <li><Say>€12</Say> and <Say say="12 euros">12 €</Say> = 12-euros, or <Say say="a 12 euro meal">a €12 meal</Say> = a 12-euro meal</li>
      <li><Say>It costs £10</Say> = It costs 10-pounds, or <Say say="a 10 pound note">a £10 note</Say> = a 10-pound note</li>
      <li><Say say="It's 90 degrees %outside">It's 90° outside</Say> = It's 90-degrees outside, or <Say say="a 90 degree angle">a 90° angle</Say> = a 90-degree angle</li>
      <li><Say>12.34%</Say> = twelve-point-three-four-percent</li>
      <li><Say say="5 feet 9 inches">5' 9"</Say> = five-feet nine-inches, but <Say>I said, "5 9"</Say> = I said five nine</li>
    </ul>

    <p>
      You may also see various units such as <Say>5kg</Say> = 5 kilograms, <Say>5lb</Say> = 5 pounds, and <Say>5mb</Say> = 5 megabytes.
      But even units can be ambiguous, of course.&nbsp;
      <i>3m</i> could be <Say>3 meters</Say> or <Say>3 million</Say> or could even refer to the company <Say say="3 m">3M</Say>.
    </p>

    <p>
      You may also see symbols like <Say>cat & dog</Say> or symbols embedded in a word like <Say say="pat @ domain dot com">pat@domain.com</Say>.
      If you're reading something that involves math, you might combine across Greek letters, like <Say>α, β</Say>.
      If you're reading linguistics perhaps you come across symbols like <Say>æ, ə, and ɤ</Say>.
    </p>

    <h2>...and more</h2>

    <p>
      And then you see an operating system called <Say say="m s /ˈdɑs">MS-DOS</Say>, where you spell out M S but pronounce the "dos",
      <Say say="o s 2">OS/2</Say> where you spell out O S and ignore the slash,
      and <Say say="be o s">BeOS</Say> where you say <i>be</i> but spell out O S.
      Or you read a book with an android named <Say say="mettrix 47 dash e l 9">METRX47-EL9</Say>.
      Or you see a discussion of sugar that talks about the molecule <Say say="2 R 3 S 4 R 5 R, 2 3 4 5 6 /ˈpɛntəˌhaɪ̯ˈdɹɑksiˈhɛksəˌnɑɫ">(2R,3S,4R,5R)-2,3,4,5,6-Pentahydroxyhexanal</Say>.
    </p>

    <p>
      The point is that being able to pronounce whatever you may find in an English article or book
      is trickier than simply being able to make sense of its quirky spelling.
      Sometimes you need to figure out how to interpret a variety of letters, numbers, and symbols all mixed together.
      And sometimes you need to understand the meaning of the sentence - or even surrounding text! - in order to guess at how a word should be pronounced.
    </p>
  </div>);
}
